import { ProfileV2 } from '@/models/profile';

// Profiles are stored per user id. Multiple users can sign in through the same browser instance
// (e.g. several Discord accounts on one desktop client) and they all share the same local storage,
// so a single shared key would hand one user's profile to the next one to sign in.
export const LS_PROFILE = 'ARISE_PROFILE'; // Legacy shared entry, moved to its owner's key on read
export const LS_PROFILE_PREFIX = `${LS_PROFILE}:`;

export function profileStorageKey(userId: string) {
    return `${LS_PROFILE_PREFIX}${userId}`;
}

export function storeProfileV2InLocalStorage(profile: ProfileV2, userId?: string) {
    const ownerId = userId || profile?.userId || '';

    if (!profile || !ownerId) {
        console.warn('storeProfileV2InLocalStorage: No user id for this profile, not storing it');
        return;
    }

    try {
        localStorage.setItem(profileStorageKey(ownerId), JSON.stringify({ ...profile, userId: ownerId }));
    } catch (error) {
        console.error('storeProfileV2InLocalStorage error: Could not store local profile', error);
    }
}

export function getProfileV2FromLocalStorage(userId: string): ProfileV2 | undefined {
    if (!userId) return undefined;

    try {
        const storedProfile = localStorage.getItem(profileStorageKey(userId)) ?? migrateLegacyProfile(userId);
        if (!storedProfile) return undefined;

        const parsed = JSON.parse(storedProfile);

        // Validate essential structure to ensure it's a real ProfileV2
        if (
            parsed &&
            typeof parsed === 'object' &&
            Array.isArray(parsed.titles) &&
            Array.isArray(parsed.badges) &&
            typeof parsed.displayName === 'string'
        ) {
            if ((parsed.userId || '') !== userId) {
                // Belongs to someone else - should not be possible now profiles are keyed by user
                console.warn('getProfileV2FromLocalStorage: Stored profile belongs to another user, discarding');
                clearProfileV2FromLocalStorage(userId);
                return undefined;
            }
            return parsed as ProfileV2;
        }

        console.warn('getProfileV2FromLocalStorage: Invalid profile format in storage');
        return undefined;
    } catch (error) {
        console.error('getProfileV2FromLocalStorage error: Could not parse local profile', error);
        return undefined;
    }
}

// One-off move of the old shared entry onto its owner's key. Returns it only if it turns out to
// belong to the user we're loading for - an entry with no owner cannot be attributed to anyone.
function migrateLegacyProfile(userId: string): string | null {
    try {
        const legacyProfile = localStorage.getItem(LS_PROFILE);
        if (!legacyProfile) return null;

        localStorage.removeItem(LS_PROFILE);

        const ownerId = JSON.parse(legacyProfile)?.userId;
        if (!ownerId || typeof ownerId !== 'string') return null;

        localStorage.setItem(profileStorageKey(ownerId), legacyProfile);
        console.log('[Arise] Migrated legacy local profile to its own user key');

        return ownerId === userId ? legacyProfile : null;
    } catch (error) {
        console.error('migrateLegacyProfile error: Could not migrate legacy local profile', error);
        return null;
    }
}

// Pass a user id to clear just that user's profile, or omit it to clear every stored profile
export function clearProfileV2FromLocalStorage(userId?: string) {
    try {
        localStorage.removeItem(LS_PROFILE);

        if (userId) {
            localStorage.removeItem(profileStorageKey(userId));
            return;
        }

        Object.keys(localStorage)
            .filter((key) => key.startsWith(LS_PROFILE_PREFIX))
            .forEach((key) => localStorage.removeItem(key));
    } catch (error) {
        console.error('clearProfileV2FromLocalStorage error: Could not clear local profile', error);
    }
}
